// src/services/DpdpService.js
// SPRINT-DPDP-DATA-RIGHTS-001 · Phase D
//
// Client-side wrapper around the DPDP erasure-workflow Cloud Functions.
// Mirrors the "service wraps one concern" pattern already established by
// DraftGenerationService.js — onCall functions via httpsCallable, kept
// separate from any pure-Firestore service.
//
// createErasureRequest and exportClientData are onRequest (not onCall) —
// they support both staff and client-portal callers with different auth
// shapes (Firebase ID token vs. magic-link JWT), which doesn't fit
// httpsCallable's single-auth-context model. Those two use the same raw
// fetch pattern already established in UserService.js's updateUser() call.

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { CF_BASE } from "../config/constants"; // v2.3.4: single shared CF_BASE (REACT_APP_CF_BASE)

async function postToFunction(path, body) {
  const response = await fetch(`${CF_BASE}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.error?.message || `HTTP ${response.status} from ${path}`);
  }
  return data;
}

export const DpdpService = {
  // ── Staff-side (onCall) ──────────────────────────────────────────────

  /**
   * Identity verification gate (ADR §8 step 2). Reviewer confirms a match
   * against the firm's client record, or manually overrides for the
   * representative-letter case after reviewing it offline.
   */
  verifyRequesterIdentity: async (firmId, requestId, manualOverrideConfirm, manualOverrideRemarks) => {
    const callable = httpsCallable(functions, "verifyRequesterIdentity");
    const result = await callable({ firmId, requestId, manualOverrideConfirm, manualOverrideRemarks });
    return result.data;
  },

  /** Re-run classification after a Needs More Information round resolves new facts. */
  rerunClassification: async (firmId, requestId) => {
    const callable = httpsCallable(functions, "manualRerunClassification");
    const result = await callable({ firmId, requestId });
    return result.data;
  },

  /**
   * Single controlled entry point for all state changes.
   * @param {string} action — 'needs_more_info' | 'return_to_review' | 'withdraw' | 'approve_execute' | 'reject'
   */
  transitionRequest: async (firmId, requestId, action, remarks) => {
    const callable = httpsCallable(functions, "transitionErasureRequest");
    const result = await callable({ firmId, requestId, action, remarks });
    return result.data;
  },

  /** Runs only from Approved — Executing. Applies the classification atomically. */
  executeErasure: async (firmId, requestId) => {
    const callable = httpsCallable(functions, "executeErasure");
    const result = await callable({ firmId, requestId });
    return result.data;
  },

  /** Firm-wide backup export (Admin/ManagingPartner only). Returns a signed ZIP URL. */
  exportFirmBackup: async (firmId) => {
    const callable = httpsCallable(functions, "exportFirmBackup");
    const result = await callable({ firmId });
    return result.data;
  },

  // ── Dual-channel (onRequest) — staff-initiated variants ─────────────

  /**
   * Staff logging an erasure request on a client's behalf (email/phone intake).
   * For the client-portal self-service variant, see requestErasureFromPortal below.
   */
  createErasureRequestAsStaff: async (staffIdToken, firmId, clientId, { requesterEmail, requesterPhone, representativeLetterUrl, remarks } = {}) =>
    postToFunction("createErasureRequest", {
      staffIdToken, firmId, clientId, requesterEmail, requesterPhone, representativeLetterUrl, remarks,
    }),

  /** Client-portal self-service export — no approval gate, read-only. */
  exportClientDataAsStaff: async (staffIdToken, firmId, clientId) =>
    postToFunction("exportClientData", { staffIdToken, firmId, clientId }),

  // ── Dual-channel (onRequest) — client-portal self-service variants ──
  // Used by DataRightsPanel.jsx (client portal). portalToken is the same
  // magic-link JWT already used by getClientData.js — decodes to
  // { firmId, clientId } server-side, so no separate ids are sent here.

  /**
   * Client submitting their own erasure request via the portal.
   * @param {string} portalToken — the client's magic-link JWT
   * @param {{requesterEmail?, requesterPhone?, representativeLetterUrl?, remarks?}} details
   */
  requestErasureFromPortal: async (portalToken, details = {}) =>
    postToFunction("createErasureRequest", { portalToken, ...details }),

  /** Client withdrawing their own (not-yet-executed) request via the portal. */
  withdrawRequestFromPortal: async (portalToken, firmId, requestId, remarks) => {
    const callable = httpsCallable(functions, "transitionErasureRequest");
    const result = await callable({ firmId, requestId, action: "withdraw", remarks, portalToken });
    return result.data;
  },

  /** Client exporting their own data — both machine-readable JSON and a lawyer-friendly ZIP link. */
  exportMyDataFromPortal: async (portalToken) =>
    postToFunction("exportClientData", { portalToken }),
};

export default DpdpService;
